import React, { useState, useEffect, useMemo } from 'react';
import {
  BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from 'recharts';
import { apiUrl, type UnitScale, unitLabel } from '../App';
import { authedFetch } from '../supabaseClient';
import KpiTooltip from '../KpiTooltip';

// ── Types ─────────────────────────────────────────────────────────────────────

interface MrrBuildPeriod {
  beginning: number;
  new: number;
  resurrection: number;
  upsell: number;
  contraction: number;
  churned: number;
  net_change: number;
  ending: number;
  net_churn_pct: number | null;
  gross_churn_pct: number | null;
}

interface CustomerBuildPeriod {
  beginning: number;
  additions: number;
  subtractions: number;
  ending: number;
  churn_pct: number | null;
}

interface MrrBuildData {
  period_cols: string[];
  period_type: string;
  filter_options: {
    segments: string[];
    revenue_types: string[];
    products: string[];
  };
  mrr_build: Record<string, MrrBuildPeriod>;
  customer_build: Record<string, CustomerBuildPeriod>;
  nrr: Record<string, number | null>;
  grr: Record<string, number | null>;
  summary: {
    total_revenue: Record<string, number>;
    run_rate_revenue: Record<string, number>;
    ttm_revenue: Record<string, number | null>;
    yoy_growth: Record<string, number | null>;
    ttm_yoy_growth: Record<string, number | null>;
    customer_count: Record<string, number>;
    total_arr: number;
  };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatPeriod(period: string): string {
  const [year, month] = period.split('-');
  return `${parseInt(month)}/${year}`;
}

let _scale = 1000;

function formatVal(val: number): string {
  if (val === 0) return '';
  const abs = Math.abs(val) / _scale;
  const str = abs < 1 ? abs.toFixed(1) : Math.round(abs).toLocaleString();
  return val < 0 ? `(${str})` : str;
}

function formatPct(val: number | null | undefined): string {
  if (val === null || val === undefined) return '';
  const rounded = Math.round(Math.abs(val));
  const str = rounded === 0 ? '0%' : rounded + '%';
  return val < 0 ? `(${str})` : str;
}

function formatPctDecimal(val: number | null | undefined): string {
  if (val === null || val === undefined) return '';
  return val.toFixed(1) + '%';
}

function formatCount(val: number | null | undefined): string {
  if (val === null || val === undefined || val === 0) return '';
  return val.toLocaleString();
}

// ── Filter Bar ───────────────────────────────────────────────────────────────

function FilterBar({
  filterOptions,
  filters,
  onFiltersChange,
}: {
  filterOptions: MrrBuildData['filter_options'];
  filters: Record<string, string>;
  onFiltersChange: (f: Record<string, string>) => void;
}) {
  const update = (key: string, value: string) => {
    const next = { ...filters };
    if (value === '') {
      delete next[key];
    } else {
      next[key] = value;
    }
    onFiltersChange(next);
  };

  const dropdowns: { label: string; key: string; options: string[] }[] = [
    { label: 'Segment',      key: 'segment',      options: filterOptions.segments },
    { label: 'Revenue Type', key: 'revenue_type',  options: filterOptions.revenue_types },
    { label: 'Product',      key: 'product',       options: filterOptions.products },
  ];

  return (
    <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-4 mb-4">
      <span className="text-gray-500 text-xs font-medium">{unitLabel(_scale as UnitScale)}</span>
      {dropdowns.map(d => (
        <div key={d.key} className="flex items-center gap-2">
          <label className="text-gray-500 text-xs font-medium">{d.label}</label>
          <select
            value={filters[d.key] || ''}
            onChange={e => update(d.key, e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-xs text-gray-200 focus:outline-none focus:border-blue-500 min-w-[120px] flex-1 md:flex-none"
          >
            <option value="">All</option>
            {d.options.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      ))}
      {Object.keys(filters).length > 0 && (
        <button
          onClick={() => onFiltersChange({})}
          className="text-gray-500 hover:text-white text-xs transition-colors ml-1"
        >
          Clear filters
        </button>
      )}
    </div>
  );
}

// ── Row Components ───────────────────────────────────────────────────────────

function SectionLabelRow({ label, periods }: { label: string; periods: string[] }) {
  return (
    <tr className="border-b border-gray-700">
      <td className="sticky left-0 z-10 bg-gray-950 px-3 md:px-5 py-3 whitespace-nowrap">
        <span className="font-bold text-white text-[11px] md:text-sm tracking-wide uppercase">{label}</span>
      </td>
      {periods.map(p => <td key={p} className="bg-gray-950" />)}
    </tr>
  );
}

function MetricGroupRow({ label, collapsed, onToggle, periods }: {
  label: string;
  collapsed: boolean;
  onToggle: () => void;
  periods: string[];
}) {
  return (
    <tr className="border-b border-gray-700 bg-[#161e2d] cursor-pointer hover:bg-[#192230]" onClick={onToggle}>
      <td className="sticky left-0 z-10 bg-[#161e2d] px-3 md:px-5 py-2.5 whitespace-nowrap">
        <div className="flex items-center gap-2">
          <span className="text-gray-500 text-[11px] md:text-xs select-none w-3 inline-block">{collapsed ? '▶' : '▼'}</span>
          <span className="font-bold text-white text-[11px] md:text-xs">{label}</span>
        </div>
      </td>
      {periods.map(p => <td key={p} className="py-2.5" />)}
    </tr>
  );
}

// ── Revenue Build Waterfall Chart ─────────────────────────────────────────────

const WATERFALL_COLORS: Record<string, string> = {
  beginning:    '#3b82f6',
  new_rev:      '#6366f1',
  resurrection: '#8b5cf6',
  upsell:       '#f97316',
  contraction:  '#34d399',
  churned:      '#ef4444',
};

const WATERFALL_LABELS: Record<string, string> = {
  beginning:    'Beginning',
  new_rev:      'New',
  resurrection: 'Resurrection',
  upsell:       'Upsell',
  contraction:  'Contraction',
  churned:      'Churned',
};

interface WaterfallBar {
  label: string;
  type: string;
  base: number;
  value: number;
}

function RevenueBuildChart({ data }: { data: MrrBuildData }) {
  const { period_cols, mrr_build } = data;

  const waterfallData = useMemo(() => {
    const bars: WaterfallBar[] = [];

    for (const p of period_cols) {
      const b = mrr_build[p];
      if (!b) continue;

      const periodLabel = formatPeriod(p);
      let running = b.beginning / _scale;

      // Beginning: bar from 0 to beginning
      bars.push({ label: periodLabel, type: 'beginning', base: 0, value: running });

      // New: floats on top of beginning
      const newVal = b.new / _scale;
      bars.push({ label: '', type: 'new_rev', base: running, value: newVal });
      running += newVal;

      // Resurrection: floats on top
      const resVal = b.resurrection / _scale;
      bars.push({ label: '', type: 'resurrection', base: running, value: resVal });
      running += resVal;

      // Upsell: floats on top
      const upsellVal = b.upsell / _scale;
      bars.push({ label: '', type: 'upsell', base: running, value: upsellVal });
      running += upsellVal;

      // Contraction: negative → step down. Base = new lower level, value = |amount|
      const contrVal = Math.abs(b.contraction / _scale);
      running -= contrVal;
      bars.push({ label: '', type: 'contraction', base: running, value: contrVal });

      // Churned: negative → step down further
      const churnVal = Math.abs(b.churned / _scale);
      running -= churnVal;
      bars.push({ label: '', type: 'churned', base: running, value: churnVal });
    }

    return bars;
  }, [period_cols, mrr_build, _scale]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || !payload[1]) return null;
    const entry = payload[1].payload as WaterfallBar;
    const typeLabel = WATERFALL_LABELS[entry.type] || entry.type;
    const isNeg = entry.type === 'contraction' || entry.type === 'churned';
    const displayVal = isNeg ? `-$${entry.value.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : `$${entry.value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    return (
      <div className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-xs shadow-lg">
        {entry.label && <p className="text-gray-400 mb-1">{entry.label}</p>}
        <p style={{ color: WATERFALL_COLORS[entry.type] }}>
          {typeLabel}: {displayVal}
        </p>
      </div>
    );
  };

  // Legend entries
  const legendItems = Object.entries(WATERFALL_LABELS).map(([key, label]) => ({
    value: label,
    color: WATERFALL_COLORS[key],
  }));

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
      <h3 className="text-sm font-bold text-white mb-4">Revenue Build</h3>
      <ResponsiveContainer width="100%" height={320}>
        <BarChart data={waterfallData} barCategoryGap={0} barGap={0}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fill: '#6b7280', fontSize: 9 }}
            interval={0}
            tickLine={false}
            angle={-45}
            textAnchor="end"
            height={50}
          />
          <YAxis
            tickFormatter={(v: number) => `$${Math.round(v).toLocaleString()}`}
            tick={{ fill: '#6b7280', fontSize: 10 }}
          />
          {/* No tooltip — chart is self-explanatory */}
          {/* Invisible base bar */}
          <Bar dataKey="base" stackId="w" fill="transparent" isAnimationActive={false} />
          {/* Visible value bar with per-cell coloring */}
          <Bar dataKey="value" stackId="w" isAnimationActive={false}>
            {waterfallData.map((entry, idx) => (
              <Cell key={idx} fill={WATERFALL_COLORS[entry.type] || '#6b7280'} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      {/* Manual legend */}
      <div className="flex items-center justify-center flex-wrap gap-2 md:gap-4 mt-2">
        {legendItems.map(item => (
          <div key={item.value} className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: item.color }} />
            <span className="text-gray-500 text-xs">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Build Table ──────────────────────────────────────────────────────────────

function BuildTable({ data, projectId }: { data: MrrBuildData; projectId: string }) {
  const { period_cols, period_type, mrr_build, customer_build, nrr, grr, summary } = data;
  const n = period_type === 'quarterly' ? 4 : 12;
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const toggle = (key: string) =>
    setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));

  // ── Row definitions ────────────────────────────────────────────────────────

  type DataRow = {
    label: string;
    render: (p: string) => string;
    indent?: boolean;
    kpiKey?: string;
  };

  const customerBuildRows: DataRow[] = [
    { label: 'Beginning',        render: p => formatCount(customer_build[p]?.beginning) },
    { label: 'Addition',         render: p => formatCount(customer_build[p]?.additions) },
    { label: 'Subtraction',      render: p => formatCount(customer_build[p]?.subtractions) },
    { label: 'Ending',           render: p => formatCount(customer_build[p]?.ending) },
    { label: 'Customer Churn %', indent: true, kpiKey: 'customer_churn_pct', render: p => {
      const v = customer_build[p]?.churn_pct;
      return v != null ? formatPct(-v) : '';
    }},
  ];

  const revBuildRows: DataRow[] = [
    { label: 'Beginning Rev',    render: p => formatVal(mrr_build[p]?.beginning ?? 0) },
    { label: 'New Rev',          render: p => formatVal(mrr_build[p]?.new ?? 0) },
    { label: 'Resurrection Rev', render: p => formatVal(mrr_build[p]?.resurrection ?? 0) },
    { label: 'Upsell Rev',       render: p => formatVal(mrr_build[p]?.upsell ?? 0) },
    { label: 'Contraction Rev',  render: p => formatVal(mrr_build[p]?.contraction ?? 0) },
    { label: 'Churned Rev',      render: p => formatVal(mrr_build[p]?.churned ?? 0) },
    { label: 'Ending Rev',       render: p => formatVal(mrr_build[p]?.ending ?? 0) },
  ];

  const rrrBuildRows: DataRow[] = [
    { label: 'Beginning RRR',    render: p => formatVal((mrr_build[p]?.beginning ?? 0) * n) },
    { label: 'New RRR',          render: p => formatVal((mrr_build[p]?.new ?? 0) * n) },
    { label: 'Resurrection RRR', render: p => formatVal((mrr_build[p]?.resurrection ?? 0) * n) },
    { label: 'Upsell RRR',       render: p => formatVal((mrr_build[p]?.upsell ?? 0) * n) },
    { label: 'Contraction RRR',  render: p => formatVal((mrr_build[p]?.contraction ?? 0) * n) },
    { label: 'Churned RRR',      render: p => formatVal((mrr_build[p]?.churned ?? 0) * n) },
    { label: 'Ending RRR',       render: p => formatVal((mrr_build[p]?.ending ?? 0) * n) },
    { label: 'Rev Net Churn %',  indent: true, kpiKey: 'mrr_net_churn_pct', render: p => formatPctDecimal(mrr_build[p]?.net_churn_pct) },
    { label: 'Rev Gross Churn %', indent: true, kpiKey: 'mrr_gross_churn_pct', render: p => formatPctDecimal(mrr_build[p]?.gross_churn_pct) },
  ];

  const retentionRows: DataRow[] = [
    { label: 'NRR (Net Revenue Retention)',   kpiKey: 'nrr', render: p => formatPctDecimal(nrr[p]) },
    { label: 'GRR (Gross Revenue Retention)', kpiKey: 'grr', render: p => formatPctDecimal(grr[p]) },
    { label: 'SaaS Quick Ratio', kpiKey: 'saas_quick_ratio', render: p => {
      const b = mrr_build[p];
      if (!b) return '';
      const pos = (b.new || 0) + (b.resurrection || 0) + (b.upsell || 0);
      const neg = Math.abs((b.contraction || 0) + (b.churned || 0));
      return neg > 0 ? (pos / neg).toFixed(2) + 'x' : '';
    }},
  ];

  const revenueMetricsRows: DataRow[] = [
    { label: 'Run-Rate Revenue',  kpiKey: 'run_rate_revenue', render: p => formatVal(summary.run_rate_revenue[p] ?? 0) },
    { label: 'TTM Revenue',       kpiKey: 'ttm_revenue', render: p => {
      const v = summary.ttm_revenue[p];
      return v != null ? formatVal(v) : '';
    }},
    { label: 'YoY Growth',        kpiKey: 'yoy_revenue_growth', render: p => formatPct(summary.yoy_growth[p]) },
    { label: 'TTM YoY Growth',    kpiKey: 'ttm_yoy_revenue_growth', render: p => formatPct(summary.ttm_yoy_growth[p]) },
  ];

  const acvRows: DataRow[] = [
    { label: 'ACV per Customer', kpiKey: 'acv_per_customer', render: p => {
      const ending = mrr_build[p]?.ending ?? 0;
      const custCount = customer_build[p]?.ending ?? 0;
      return custCount > 0 ? formatVal((ending * n) / custCount) : '';
    }},
    { label: 'New ACV per New Customer', kpiKey: 'new_acv_per_new_customer', render: p => {
      const newRev = mrr_build[p]?.new ?? 0;
      const newCust = customer_build[p]?.additions ?? 0;
      return newCust > 0 ? formatVal((newRev * n) / newCust) : '';
    }},
  ];

  // ── Render helper ──────────────────────────────────────────────────────────

  const renderRows = (rows: DataRow[]) =>
    rows.map(row => (
      <tr key={row.label} className="border-b border-gray-800/60 hover:bg-gray-800/30">
        <td className={`sticky left-0 z-10 bg-gray-900 ${row.indent ? 'pl-6 md:pl-[68px]' : 'pl-5 md:pl-[52px]'} pr-3 md:pr-5 py-2 text-gray-300 text-[11px] md:text-xs md:whitespace-nowrap`}>
          {row.label}
        </td>
        {period_cols.map(p => (
          <td key={p} className="text-right px-3 md:px-6 py-2 text-gray-300 text-[11px] md:text-xs whitespace-nowrap">
            {row.kpiKey
              ? <KpiTooltip kpiKey={row.kpiKey} projectId={projectId}>{row.render(p)}</KpiTooltip>
              : row.render(p)}
          </td>
        ))}
      </tr>
    ));

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg">
      <div className="overflow-auto max-h-[calc(100vh-10rem)]">
        <table className="w-full text-xs">

          {/* Sticky date header */}
          <thead className="sticky top-0 z-20">
            <tr className="border-b border-gray-700 bg-gray-900">
            <th className="sticky left-0 z-30 bg-gray-900 text-left px-3 md:px-5 py-2.5 w-28 md:w-52"></th>
              {period_cols.map(p => (
                <th key={p} className="bg-gray-900 text-right px-3 md:px-6 py-2.5 font-bold text-gray-200 text-[11px] md:text-sm whitespace-nowrap min-w-[72px]">
                  {formatPeriod(p)}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>

            {/* Customer Build */}
            <MetricGroupRow
              label="Customer Build"
              collapsed={!!collapsed['custbuild']}
              onToggle={() => toggle('custbuild')}
              periods={period_cols}
            />
            {!collapsed['custbuild'] && renderRows(customerBuildRows)}

            {/* Revenue Build */}
            <MetricGroupRow
              label="Revenue Build"
              collapsed={!!collapsed['revbuild']}
              onToggle={() => toggle('revbuild')}
              periods={period_cols}
            />
            {!collapsed['revbuild'] && renderRows(revBuildRows)}

            {/* Run-Rate Revenue Build */}
            <MetricGroupRow
              label="Run-Rate Revenue Build"
              collapsed={!!collapsed['rrrbuild']}
              onToggle={() => toggle('rrrbuild')}
              periods={period_cols}
            />
            {!collapsed['rrrbuild'] && renderRows(rrrBuildRows)}

            {/* Revenue Retention */}
            <MetricGroupRow
              label="Revenue Retention"
              collapsed={!!collapsed['retention']}
              onToggle={() => toggle('retention')}
              periods={period_cols}
            />
            {!collapsed['retention'] && renderRows(retentionRows)}

            {/* Revenue Metrics */}
            <MetricGroupRow
              label="Revenue Metrics"
              collapsed={!!collapsed['revmetrics']}
              onToggle={() => toggle('revmetrics')}
              periods={period_cols}
            />
            {!collapsed['revmetrics'] && renderRows(revenueMetricsRows)}

            {/* ACVs */}
            <MetricGroupRow
              label="ACVs"
              collapsed={!!collapsed['acvs']}
              onToggle={() => toggle('acvs')}
              periods={period_cols}
            />
            {!collapsed['acvs'] && renderRows(acvRows)}

          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Main View ────────────────────────────────────────────────────────────────

export default function MrrBuildView({
  projectId,
  filters,
  onFiltersChange,
  unitScale,
}: {
  projectId: string;
  filters: Record<string, string>;
  onFiltersChange: (f: Record<string, string>) => void;
  unitScale: UnitScale;
}) {
  _scale = unitScale;
  const [data, setData]       = useState<MrrBuildData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    const extra: Record<string, string> = {};
    if (filters.segment)      extra.segment = filters.segment;
    if (filters.revenue_type) extra.revenue_type = filters.revenue_type;
    if (filters.product)      extra.product = filters.product;

    authedFetch(apiUrl('/api/customers/analytics', projectId, extra))
      .then(res => {
        if (!res.ok) throw new Error(res.status === 404 ? 'No customer data' : 'Fetch failed');
        return res.json();
      })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [projectId, filters]);

  if (loading) return <p className="text-gray-500 text-sm">Loading...</p>;
  if (error) return (
    <div className="text-center py-16">
      <p className="text-gray-500 text-sm">{error === 'No customer data' ? 'No customer revenue data uploaded for this project.' : error}</p>
    </div>
  );
  if (!data) return null;

  return (
    <div className="space-y-4">
      <FilterBar
        filterOptions={data.filter_options}
        filters={filters}
        onFiltersChange={onFiltersChange}
      />
      <RevenueBuildChart data={data} />
      <BuildTable data={data} projectId={projectId} />
    </div>
  );
}
